import BN from 'bn.js'
import { useMutation, useQueryClient } from 'react-query'

import { useSolana } from '../providers/solana'
import { convertUIAmountToRawAmount } from '../utils/bn-utils'
import { LAMPORTS_PER_SOL, PublicKey, SystemProgram } from '@solana/web3.js'
import { useProjects } from './use-projects'
import { logStakingPosition } from '../utils/apis/staking-positions'
import { getProjectByAddress } from '../utils/fetch-projects'
import bs58 from 'bs58'

export function useStake() {
  const { stakingClient, connection, publicKey, signTransaction, usingJitoValidator, jitoClient, jitoTipAmount } =
    useSolana()
  const queryClient = useQueryClient()

  const { projects } = useProjects()

  const mutation = useMutation<string | undefined, unknown, [string, string]>(
    async ([tokenAddress, amount]) => {
      if (!stakingClient || !connection || !publicKey || !signTransaction) {
        return
      }

      const project = projects.find((project) => project.address === tokenAddress)

      if (!project) {
        return
      }

      const stakeAmount = convertUIAmountToRawAmount(amount, project.decimals)
      const tx = await stakingClient.stake(new BN(stakeAmount), true, new PublicKey(tokenAddress))
      if (usingJitoValidator) {
        const randomTipAccount = await jitoClient.getRandomTipAccount()
        const jitoTipAccount = new PublicKey(randomTipAccount)

        tx.add(
          SystemProgram.transfer({
            fromPubkey: publicKey,
            toPubkey: jitoTipAccount,
            lamports: jitoTipAmount * LAMPORTS_PER_SOL,
          }),
        )
      }

      const latestBlockHash = await connection.getLatestBlockhash()
      tx.recentBlockhash = latestBlockHash.blockhash
      tx.feePayer = publicKey

      const signedTx = await signTransaction(tx)
      const serializedTx = signedTx.serialize()
      let signature

      if (usingJitoValidator) {
        const result = await jitoClient.sendTxn([bs58.encode(new Uint8Array(serializedTx))], false)
        signature = result?.result
      } else {
        signature = await connection.sendRawTransaction(serializedTx)
      }

      await connection.confirmTransaction({
        blockhash: latestBlockHash.blockhash,
        lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
        signature: signature,
      })

      return signature
    },
    {
      onSuccess: async (signature, [tokenAddress, amount]) => {
        queryClient.invalidateQueries(['userFlexibleStakingInfo', publicKey])
        queryClient.invalidateQueries(['protocolBasicInfo', tokenAddress])
        queryClient.invalidateQueries(['tokenBalance', tokenAddress, publicKey?.toString() || ''])

        const project = getProjectByAddress(tokenAddress)

        if (project && signature) {
          // Log staking position
          await logStakingPosition({
            wallet_address: publicKey?.toString() || '',
            action: 'deposit',
            amount: Number(amount),
            tx_hash: signature,
            symbol: project.symbol,
            stake_type: 'flexible',
            vault_index: 0,
          })
        }
      },
    },
  )

  return mutation
}
